<template>
  <app-layout :has-sidebar="isGradingTemplate">
    <template #header>
      <div class="flex items-center">
        <div class="flex-1">
          <h1 class="text-lg flex items-center">
            <span
              v-if="state.project.stream && !isSingleStream"
              class="border rounded px-2 py-1 align-center text-xs inline-block leading-none mr-2"
              :style="getStreamColorsByKey(state.project.stream)"
            >
              {{ getStreamNameByKey(state.project.stream) }}
            </span>
            {{ state.project.ref }}
          </h1>
          <p class="text-grey-700 hidden sm:block text-sm">
            {{ state.marking_phase.name }} Form
          </p>
        </div>
        <div class="ml-6 self-center flex">
          <a
            :href="route(`assessments.phases.view`, [state.funding_pot.id, state.marking_phase.id])"
            class="transition-all transition-normal inline-flex items-center px-4 py-3 border-2 border-grey-300 text-grey-800 shadow text-sm font-semibold bg-white hover:bg-gray-50 focus:outline-none focus:shadow-outline no-underline hover:bg-primary-100 hover:border-primary-300 leading-none ml-2"
          >
            Back
          </a>
        </div>
      </div>
      <div
        v-if="tabs.length > 1 && isGradingTemplate"
        class="flex items-center justify-between border-t mt-4 -mb-4"
      >
        <ul class="list-reset flex">
          <li v-for="(tab, index) in tabs" :key="index" class="mr-8">
            <a
              :href="'#' + tab.hash"
              class="inline-block py-4 border-b-2 -mb-0.5 no-underline"
              :class="{
                'border-primary-500 text-primary-600 font-semibold':
                  active === index,
                'border-transparent text-grey-600 hover:text-grey-900 hover:border-grey-300':
                  active !== index
              }"
              @click.stop.prevent="setActive(index)"
            >
              {{ tab.name }}
            </a>
          </li>
        </ul>
      </div>
    </template>
    <div v-if="isGradingTemplate" class="flex">
      <div class="container">
        <div
          v-if="gradingLocked"
          class="mt-6"
        >
          <div class="bg-neutral-100 border border-neutral-300 text-neutral-800 pl-4 pr-6 py-2 flex items-center">
            <icon
              icon="important"
              class="w-6 h-6 icons-neutral mr-2"
            />
            <p>
              <template v-if="state.marking_phase.locked">
                Assessments for this phase are currently locked. If you believe that this is incorrected, please contact <a
                  :href="$config.emails.technical"
                  class="text-black hover:border-neutral-800"
                >technical support</a>.
              </template>
              <template v-else>
                This assessment is no longer available for grading and scores cannot be changed.
              </template>
            </p>
          </div>
        </div>
        <form
          @submit.prevent="() => {}"
        >
          <application-section
            :fieldset="fieldsets[active]"
            :errors="errors"
            :show-errors="showErrors"
          />
        </form>
        <ul class="list-reset flex justify-between items-baseline">
          <li class="w-1/3">
            <icon-link
              v-if="active > 0"
              :href="'#' + tabs[active - 1].hash"
              icon="arrow-thin-left-circle"
              @click.stop.prevent="setActive(active - 1)"
            >
              Previous: {{ tabs[active - 1].name }}
            </icon-link>
            <icon-link
              v-if="active === 'feedback'"
              :href="'#' + tabs[tabs.length - 1].hash"
              icon="arrow-thin-left-circle"
              @click.stop.prevent="setActive(tabs.length - 1)"
            >
              Previous: {{ tabs[tabs.length - 1].name }}
            </icon-link>
          </li>
          <li class="w-1/3 text-center">
            <icon-link
              :href="route(`assessments.phases.view`, [state.funding_pot.id, state.marking_phase.id])"
              class="text-positive-600 hover:text-positive-800"
            >
              All Assessments
            </icon-link>
          </li>
          <li class="w-1/3 text-right">
            <icon-link
              v-if="active + 1 < tabs.length"
              :href="'#' + tabs[active + 1].hash"
              icon="arrow-thin-right-circle"
              icon-placement="right"
              @click.stop.prevent="setActive(active + 1)"
            >
              Next: {{ tabs[active + 1].name }}
            </icon-link>
          </li>
        </ul>
      </div>
      <assessment-sidebar
        :assessment-template="markingPhase.template"
        :style="{'margin-top': tabs.length > 1 ? 'calc(-8.75rem + 1px)' : '-5rem'}"
      />
    </div>
    <div v-else>
      <assessment-form :assessment-template="markingPhase.template" />
    </div>
  </app-layout>
</template>

<script>
import kebabCase from "lodash/kebabCase";
import sortBy from "lodash/sortBy";
import merge from "lodash/merge";
import pick from "lodash/pick";
import { format, isBefore, parseISO } from "date-fns";
import { store } from "../store/application";
import { competitionStreamsMixin } from "../mixins/competitionStreams";

export default {
  name: "AssessmentView",
  mixins: [competitionStreamsMixin],
  data() {
    return {
      loading: false,
      confirmationModalVisible: false,
      active: 0,
      showApplication: false,
      application: store.application,
      errors: store.errors,
      showErrors: store.showErrors
    };
  },
  computed: {
    gradingLocked() {
      return this.state.marking_phase.locked || this.state.project.marking_phase_status.confirmed_at
    },
    markingPhase() {
      return this.state.marking_phase
    },
    competitionYear() {
      return format(
        parseISO(this.state.competition.application_start_date),
        "yyyy"
      );
    },
    competitionHash() {
      return kebabCase(this.state.competition.title);
    },
    singleStageGrading() {
      return this.state.competition.single_stage_grading === 1;
    },
    showFeedbackTab() {
      return this.singleStageGrading
        ? this.state.project.awarded_at ||
            this.state.project.rejected_at ||
            this.state.project.shortlisted_at
        : this.state.project.feedback;
    },
    /**
     * competitionForm: checks whether it exisits and parses the JSON
     * @returns	{object}			The parsed json, if it exisits
     */
    competitionForm() {
      if (!this.state.competition.application_template) return [];
      return JSON.parse(this.state.competition.application_template);
    },
    /**
     * fieldsets: checks that the application form has fieldsets
     * @returns	{array}			Returns and array of fieldsets
     */
    fieldsets() {
      if (!this.competitionForm.fieldset) return [];
      return this.competitionForm.fieldset;
    },
    /**
     * tabs: used to create usable data for each of the tabs
     * @returns	{array}		contains each of the fieldset's name and hash
     */
    tabs() {
      if (!this.fieldsets) return [];
      return this.fieldsets.map(item =>
        Object.assign(
          {},
          {
            name: item.legend,
            hash: this.hashFriendly(item.legend)
          }
        )
      );
    },
    hasErrors() {
      return Object.keys(this.errors).length > 0;
    },
    groupedErrors() {
      const keys = Object.keys(this.errors).map(i => i.split("."));
      const fieldInput = keys.reduce((obj, item) => {
        let field = this.getFieldset(item[0]);
        if (field.legend in obj === false) {
          obj[field.legend] = [];
        }
        if (obj[field.legend].indexOf(field.question) === -1) {
          obj[field.legend].push(field.question);
        }
        return obj;
      }, {});
      return fieldInput;
    },
    reviewMode() {
      return store.reviewMode;
    },
    now() {
      return new Date();
    },
    hasPassed() {
      if (this.state.project.application_extended_until !== null) {
        return isBefore(this.application_extended_until, this.now);
      }
      return isBefore(this.application_end_date, this.now);
    },
    application_end_date() {
      return parseISO(this.state.competition.application_end_date);
    },
    application_extended_until() {
      if (!this.state.project.application_extended_until) return;
      return parseISO(this.state.project.application_extended_until);
    },
    optional_fields() {
      const fields = this.fieldsets.reduce((arr, field) => {
        arr = [...arr, ...field.fields];
        return arr;
      }, []);
      return fields.reduce((obj, input) => {
        const optional = input.sub_fields.reduce((arr, f) => {
          if(f.type === "break" || f.type === "categories") return arr;
          if (!f.attributes.required) {
            if (
              !this.application[input.key][f.key] ||
              !this.application[input.key][f.key].length
            ) {
              arr.push(f.label);
            }
          }
          return arr;
        }, []);

        if (
          input.repeatable &&
          input.repeatable.min === 0 &&
          !this.application[input.key].length
        ) {
          obj[input.key] = input.label;
        }

        if (optional.length) {
          obj[input.key] = optional.join(", ");
        }
        return obj;
      }, {});
    },
    isGradingTemplate() {
      return this.markingPhase.template?.ui?.application ?? true;
    },
  },
  created() {
    document.title = `${this.state.project.ref} Application Form :: ${
      document.title
    }`;

    window._temp = this.state.project.application;
    window._project = this.state.project.uuid;
    store.universities = this.state?.universities || [];

    this.$once("hook:beforeDestroy", () => {
      delete window._temp;
      delete window._project;
      store.reviewMode = false;
      store.showErrors = false;
      store.application = {};
      store.errors = {};
      store.universities = [];
    });

    store.reviewMode = true;

    if (this.competitionForm) {
      const fields = sortBy(this.competitionForm.fieldset, ["order"]);
      this.setApplication(fields);
      this.$once("hook:mounted", () => {
        this.showApplication = true;
      });
    }

    if (this.application && window._temp) {
      if (
        this.application.hasOwnProperty("project_partners") &&
        window._temp.hasOwnProperty("project_partners")
      ) {
        this.application.project_partners = [...window._temp.project_partners];
      }
    }

    this.loadTabWithHash(window.location.hash);
  },
  methods: {
    loadTabWithHash(hash) {
      if (!hash) return;
      hash = hash.replace("#", "");
      const index =
        hash === "feedback"
          ? "feedback"
          : this.tabs.findIndex(tab => tab.hash === hash);
      if (index < 0) return;
      return this.setActive(index);
    },
    getFieldset(key) {
      const groups = this.competitionForm.fieldset.map(item => {
        const errorField = item.fields.find(field => field.key === key);
        if (errorField)
          return { legend: item.legend, question: errorField.label };
      });
      const group = groups.filter(Boolean);
      return group[0];
    },
    /**
     * hashFriendly: creates a url friendly string of the value
     * @param		{string}	value	the value that will be "Kebabbed"
     * @return	{string}				the kebabbed string e.g. "this-is-kebabbed"
     */
    hashFriendly(value) {
      return kebabCase(value);
    },
    /**
     * setActive: used to set the the active fieldset of the application
     * @param	{int}	index	the index of the fieldset
     */
    setActive(index) {
      window.location.hash = this.tabs[index] ? this.tabs[index].hash : index;
      this.scrollTop();
      this.saveApplication;
      this.active = index;
    },
    /**
     * setApplication: this is used to set the initial values of the
     * application on load. This will then merge the users saved application
     * to the result.
     * @param {object}	application	The application form object
     * @return											Merge the empty application with the users application
     */
    setApplication(application) {
      // set initial defaults

      // Get all the application forms fields
      application.map(({ fields }) =>
        // loop over all the subfields and set the parent key
        fields.map(field => {
          /**
           * check for repeatable field
           * TODO: look at creating seperate methods for each and reduce side-effects
           */
          if (!field.hasOwnProperty("repeatable")) {
            this.$set(this.application, field.key, {});
            return this.setApplicationFieldKeyVal(field);
          } else {
            this.$set(this.application, field.key, []);
            return this.setApplicationFieldArray(field);
          }
        })
      );

      // merge with saved application
      return merge(this.application, window._temp);
    },
    checkDefault(defaultValue) {
      if (typeof defaultValue.match !== "function") return defaultValue;
      if (!defaultValue.match(/\[[A-Z]+\]/g)) return defaultValue;
      return this.matchedValue(defaultValue);
    },
    matchedValue(defaultValue) {
      const value = defaultValue.replace(/[\s\W]+/g, "").toLowerCase();
      const key = Object.freeze({
        company: {
          company_number: this.state.user.company.number,
          company_details: pick(this.state.user.company, [
            "name",
            "address_line_1",
            "address_line_2",
            "town",
            "county",
            "postcode"
          ])
        },
        name: this.state.user.name,
        email: this.state.user.email,
        telephone: this.state.user.telephone || "",
        jobtitle: this.state.user.job_title || ""
      });
      return key[value];
    },
    /**
     * setApplicationFieldKeyVal: if the form section is not multiple/repeatable,
     * then do a straight set.
     * @param	{object}	field	the individual field for the current fieldgroup
     * @return								Side-effect of updating the the user's application with the default attributes defined on the form.
     */
    setApplicationFieldKeyVal(field) {
      return field.sub_fields.map(({ key, type, attributes }) => {
        if(type === 'break' || type === 'categories') return
        return this.$set(
          this.application[field.key],
          key,
          this.checkDefault(attributes.default)
        );
      });
    },
    /**
     * setApplicationFieldKeyArray: Similar to setApplicationFieldKeyVal, the method maps each of the keys to a temp object before setting the minimum amount to the array.
     * @param	{object}	field	the individual field for the current fieldgroup
     * @return								Side-effect of updating the the user's application with the default attributes defined on the form the minimum number of entries required.
     */
    setApplicationFieldArray(field) {
      const temp = {};
      field.sub_fields.map(({ key, attributes }) => {
        return (temp[key] = attributes.default);
      });

      while (this.application[field.key].length < field.repeatable.min) {
        this.application[field.key].push({ ...temp });
      }
    },
    approve() {
      // send approval route(funding.eoi.update, { funding_pot.id, application.this.application.uuid })
      this.loading = true
      axios.post(this.route('funding.eoi.update', {
        funding: this.state.competition.funding_pots[0].id,
        application: this.state.project.uuid
      }), {
        status: 'accept'
      }).then(() => Turbolinks.visit(window.location.pathname))
      .catch(() => this.loading = false)
    },
    reject() {
      this.loading = true
      axios.post(this.route('funding.eoi.update', {
        funding: this.state.competition.funding_pots[0].id,
        application: this.state.project.uuid
      }), {
        status: 'reject',
        reject_keys: this.selectedRejectKeys,
      }).then(() => Turbolinks.visit(window.location.pathname))
      .catch(() => this.loading = false)
    }
  }
};
</script>
